import { useMemo, useState } from "react";
import { useAllItems, useProjects, useAllPhases } from "@/hooks/useData";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { useIsMobile } from "@/lib/useMediaQuery";
import { parseDate, startOfDay, scheduledFor } from "@/lib/format";
import { itemProjectId } from "@/components/ItemRow";
import type { Item, Project } from "@/lib/types";
import { TYPE_META } from "@/lib/types";
import { Dot, StatusPill } from "@/components/atoms";
import { useChrome } from "@/context/ChromeContext";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Month cursor and selected day, shared by the page and the project tab so
 *  each can place the ‹ › Today controls where its own header lives. */
export function useCalendarNav() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [selected, setSelected] = useState<string | null>(null);
  const todayKey = startOfDay().toISOString().slice(0, 10);
  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const move = (delta: number) => { const d = new Date(cursor); d.setMonth(cursor.getMonth() + delta); setCursor(d); setSelected(null); };
  const goToday = () => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); setCursor(d); setSelected(todayKey); };
  return { cursor, selected, setSelected, monthLabel, move, goToday };
}

export type CalendarNav = ReturnType<typeof useCalendarNav>;

export function CalendarNavButtons({ nav }: { nav: CalendarNav }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => nav.move(-1)} style={navBtn}>‹</button>
      <button onClick={() => nav.move(1)} style={navBtn}>›</button>
      <button onClick={nav.goToday} style={{ ...navBtn, width: "auto", padding: "0 12px", fontSize: 12.5 }}>Today</button>
    </div>
  );
}

export function CalendarView() {
  const { data: items = [] } = useAllItems();
  const mobile = useIsMobile();
  const nav = useCalendarNav();

  useChrome(
    {
      title: "Calendar",
      subtitle: nav.monthLabel,
      actions: <CalendarNavButtons nav={nav} />,
    },
    [nav.monthLabel]
  );

  return (
    <div style={{ padding: mobile ? "16px 12px 90px" : "20px 36px 90px", maxWidth: 1180 }}>
      <MonthCalendar items={items} nav={nav} mobile={mobile} />
    </div>
  );
}

/**
 * The month grid and its day panel. On the Calendar page it shows every
 * project with the legend as a filter; given `projectId` (a project's own
 * Calendar tab) it shows only the items passed in and drops the legend.
 */
export function MonthCalendar({ items, nav, mobile, projectId }: {
  items: Item[]; nav: CalendarNav; mobile: boolean; projectId?: string;
}) {
  const { data: projects = [] } = useProjects();
  const { openItem, openNew } = useTaskEditor();
  const { data: phases = [] } = useAllPhases();
  // The legend doubles as a filter: click a project to see only its dates,
  // click again to clear. One control, so there is no second place to look.
  const [onlyProject, setOnlyProject] = useState<string | null>(null);
  const phaseName = useMemo(() => new Map(phases.map((ph) => [ph.id, ph.name])), [phases]);
  const { cursor, selected, setSelected, monthLabel } = nav;

  const projectById = useMemo(() => new Map<string, Project>(projects.map((p) => [p.id, p])), [projects]);

  const visible = useMemo(
    () => (onlyProject ? items.filter((it) => itemProjectId(it) === onlyProject) : items),
    [items, onlyProject]
  );

  const byDay = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of visible) {
      const d = parseDate(scheduledFor(it));
      if (!d) continue;
      const key = d.toISOString().slice(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(it);
    }
    return m;
  }, [visible]);

  const cells = useMemo(() => {
    const first = new Date(cursor);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [cursor]);

  const todayKey = startOfDay().toISOString().slice(0, 10);

  const selectedItems = selected ? (byDay.get(selected) || []) : [];

  // Items due within the visible month, grouped by day — the side panel's
  // default content when no single day is selected.
  const monthByDay = useMemo(() => {
    const y = cursor.getFullYear(), mo = cursor.getMonth();
    const m = new Map<string, Item[]>();
    for (const it of visible) {
      const d = parseDate(scheduledFor(it));
      if (!d || d.getFullYear() !== y || d.getMonth() !== mo) continue;
      const k = d.toISOString().slice(0, 10);
      (m.get(k) || m.set(k, []).get(k)!).push(it);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible, cursor]);
  const monthCount = monthByDay.reduce((n, [, list]) => n + list.length, 0);

  return (
    <div>
      {/* Project legend — also the filter */}
      {!projectId && (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, margin: "16px 0 6px", alignItems: "center" }}>
        {projects.filter((p) => p.kind !== "inbox" && p.status !== "archived").map((p) => {
          const on = onlyProject === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setOnlyProject(on ? null : p.id)}
              aria-pressed={on}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px",
                borderRadius: 999, cursor: "pointer", fontSize: 12,
                border: `1px solid ${on ? "var(--accentLine)" : "var(--line2)"}`,
                background: on ? "var(--accentTint)" : "var(--surface)",
                color: on ? "var(--accent)" : "var(--ink3)",
                opacity: onlyProject && !on ? 0.55 : 1,
              }}
            >
              <Dot color={p.color} size={7} />{p.name}
            </button>
          );
        })}
        {onlyProject && (
          <button
            onClick={() => setOnlyProject(null)}
            style={{ border: "none", background: "none", color: "var(--accent)", cursor: "pointer", fontSize: 12, textDecoration: "underline", padding: "4px 2px" }}
          >
            Show all projects
          </button>
        )}
      </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 420px", minWidth: 0 }}>
      {/* Weekday header */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 1, marginBottom: 1 }}>
        {WEEKDAYS.map((w) => (
          <div key={w} className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted2)", padding: "6px 8px", textAlign: mobile ? "center" : "left" }}>
            {mobile ? w[0] : w}
          </div>
        ))}
      </div>

      {/* Grid — whole cell is clickable to reveal the day's detail */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 6 }}>
        {cells.map((d, i) => {
          const key = d.toISOString().slice(0, 10);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = key === todayKey;
          const isSelected = key === selected;
          const dayItems = byDay.get(key) || [];
          return (
            <button
              key={i}
              onClick={() => setSelected(isSelected ? null : key)}
              style={{
                textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit", minWidth: 0,
                minHeight: mobile ? 58 : 112,
                background: isSelected ? "var(--accentTint2)" : inMonth ? "var(--surface)" : "var(--bg2)",
                border: `1px solid ${isToday ? "var(--accentSoft)" : "var(--line)"}`,
                // §5.1 — inset, so selecting a day never shifts the grid.
                boxShadow: isSelected ? "inset 0 0 0 2px var(--accent)" : "none",
                borderRadius: 9, padding: mobile ? 4 : 8, overflow: "hidden",
                opacity: inMonth ? 1 : 0.55,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span className="mono" style={{ width: 24, height: 24, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: isToday ? "var(--fw-title)" : "var(--fw-medium)", background: isToday ? "var(--accent)" : "transparent", color: isToday ? "var(--onAccent)" : "var(--ink3)" }}>{d.getDate()}</span>
                {dayItems.length > (mobile ? 0 : 3) && <span className="mono" style={{ fontSize: 9, color: "var(--muted2)" }}>{dayItems.length}</span>}
              </div>
              <div style={{ display: "flex", flexDirection: mobile ? "row" : "column", flexWrap: "wrap", gap: 3 }}>
                {mobile
                  ? dayItems.slice(0, 4).map((it) => <MobileDot key={it.id} item={it} projectById={projectById} />)
                  : dayItems.slice(0, 3).map((it) => <Chip key={it.id} item={it} projectById={projectById} />)}
                {!mobile && dayItems.length > 3 && <span style={{ fontSize: 10, color: "var(--muted2)", paddingLeft: 2 }}>+{dayItems.length - 3} more</span>}
              </div>
            </button>
          );
        })}
      </div>
        </div>

        {/* §5.2 — the day panel. One panel for every width: the row wraps it
            below the grid when there is no room beside it. */}
        <aside style={{
          flex: "0 0 300px", minWidth: 0, alignSelf: "flex-start",
          background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14,
          boxShadow: "var(--shadowCard)", overflow: "hidden", display: "flex", flexDirection: "column",
          maxHeight: "calc(100vh - 150px)",
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 16px", borderBottom: "1px solid var(--line)" }}>
            <div style={{ minWidth: 0 }}>
              <div className="mono" style={{ fontSize: 11, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--muted)" }}>
                {selected ? weekdayOf(selected) : "This month"}
              </div>
              <div style={{ fontSize: 16, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-tight)", marginTop: 2 }}>
                {selected ? dateOf(selected) : monthLabel}
              </div>
            </div>
            <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted2)" }}>{selected ? selectedItems.length : monthCount}</span>
            {selected && (
              <button onClick={() => setSelected(null)} title="Close" aria-label="Close" style={{ background: "none", border: "none", color: "var(--muted2)", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
            )}
          </div>

          <div style={{ overflowY: "auto", padding: selected ? 8 : 0 }}>
            {selected ? (
              selectedItems.length === 0
                ? <div style={sidePanelEmpty}>Nothing due this day.</div>
                : selectedItems.map((it) => (
                  <DayItemRow key={it.id} item={it} projectById={projectById} phaseName={phaseName} onOpen={openItem} />
                ))
            ) : (
              monthByDay.length === 0
                ? <div style={sidePanelEmpty}>Nothing due this month.</div>
                : monthByDay.map(([key, list]) => (
                  <div key={key}>
                    <div style={{ position: "sticky", top: 0, background: "var(--bg2)", padding: "6px 16px", fontSize: 11, fontWeight: 500, color: key === todayKey ? "var(--accent)" : "var(--muted)", borderBottom: "1px solid var(--tint2)" }}>{shortDay(key)}</div>
                    {list.map((it) => <DayItemRow key={it.id} item={it} projectById={projectById} phaseName={phaseName} onOpen={openItem} flush />)}
                  </div>
                ))
            )}
          </div>

          {selected && (
            <div style={{ padding: 10, borderTop: "1px solid var(--line)" }}>
              <button
                onClick={() => openNew(projectId, undefined, selected)}
                style={{ width: "100%", padding: "9px 14px", borderRadius: 999, border: "none", background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, fontWeight: 500, cursor: "pointer" }}
              >
                + Item on {dateOf(selected)}
              </button>
            </div>
          )}
        </aside>
      </div>

    </div>
  );
}

function Chip({ item, projectById }: { item: Item; projectById: Map<string, Project> }) {
  const color = projectById.get(itemProjectId(item) || "")?.color || "var(--muted2)";
  const done = item.status === "done";
  return (
    <span
      title={item.title}
      style={{
        display: "flex", alignItems: "center", gap: 5, width: "100%",
        padding: "2px 5px", borderRadius: 5, background: "var(--tint)",
        fontSize: 10.5, color: done ? "var(--muted)" : "var(--ink2)",
        textDecoration: done ? "line-through" : "none",
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, flex: "none" }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</span>
    </span>
  );
}

function MobileDot({ item, projectById }: { item: Item; projectById: Map<string, Project> }) {
  const color = projectById.get(itemProjectId(item) || "")?.color || "var(--muted2)";
  return <span title={item.title} style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />;
}

function DayItemRow({ item, projectById, phaseName, onOpen, flush }: {
  item: Item; projectById: Map<string, Project>; phaseName: Map<string, string>;
  onOpen: (i: Item) => void; flush?: boolean;
}) {
  const project = projectById.get(itemProjectId(item) || "");
  const done = item.status === "done";
  return (
    <div
      onClick={() => onOpen(item)}
      style={flush
        ? { display: "flex", alignItems: "center", gap: 9, padding: "11px 16px", borderBottom: "1px solid var(--tint2)", cursor: "pointer" }
        : { display: "flex", alignItems: "center", gap: 9, padding: "9px 10px", borderRadius: 12, border: "1px solid var(--line)", background: "var(--surface)", marginBottom: 6, cursor: "pointer" }}
    >
      <span style={{ color: TYPE_META[item.type].color, fontSize: 12, flex: "none" }}>{TYPE_META[item.type].glyph}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13, textDecoration: done ? "line-through" : "none", color: done ? "var(--muted)" : "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</span>
        <span style={{ display: "block", fontSize: 11.5, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {phaseName.get(item.phase) || project?.name || ""}
        </span>
      </span>
      <StatusPill status={item.status} />
    </div>
  );
}

function weekdayOf(key: string): string {
  return new Date(key + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" });
}

function dateOf(key: string): string {
  return new Date(key + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function longDay(key: string): string {
  const d = new Date(key + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

function shortDay(key: string): string {
  const d = new Date(key + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

const sidePanelEmpty: React.CSSProperties = { padding: "20px 16px", fontSize: 12.5, color: "var(--muted2)", textAlign: "center" };

const navBtn: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)",
  color: "var(--ink2)", cursor: "pointer", fontSize: 14, display: "inline-flex", alignItems: "center", justifyContent: "center",
};
